/**
 * RecordLinkTypeFormModal — Settings → Typy powiązań.
 *
 * Modal create/edit dla wiersza słownika `record_link_types` (patrz
 * `apps/api/app/api/record-link-types/route.ts` + `[id]/route.ts`).
 * - `mode='create'` → wszystkie pola edytowalne.
 * - `mode='edit'` → kod readonly (immutable po stronie backendu — PATCH z inną
 *   wartością `code` niż zapisana kończy się 400 `CODE_IMMUTABLE`). Wiersze
 *   `isSystem=true` MOGĄ mieć edytowane etykiety/kolor/ikonę/typy/isActive/sortOrder —
 *   jedyna twarda blokada dla systemowych to kod (patrz docstring backendu).
 *
 * `reverseLabel` jest disabled gdy `isSymmetric` — dla typu symetrycznego kierunek
 * powrotny nie ma sensu (backend i tak akceptuje `null`), UI nie powinno sugerować
 * że wartość ma znaczenie.
 *
 * Walidacja (mirror backendowych regexów w `route.ts`):
 * - code: `^[a-z][a-z0-9_]{0,48}$` (tylko w trybie create — w edit pole disabled)
 * - forwardLabel: 1-100 znaków
 * - reverseLabel: max 100 znaków (opcjonalne)
 * - color: hex `#rrggbb` (opcjonalny)
 * - icon: nazwa ikony Lucide wybierana z IconPicker (opcjonalna; max 50 znaków)
 * - sortOrder: liczba całkowita
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { RecordLinkCardinality, RecordLinkOwnerType } from '@saas/shared';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { IconPicker } from '@/components/modules/IconPicker';
import { MultiSelect } from '@/components/MultiSelect';

export interface RecordLinkTypeFormValues {
  code: string;
  forwardLabel: string;
  reverseLabel: string;
  isSymmetric: boolean;
  cardinality: RecordLinkCardinality;
  color: string;
  icon: string;
  sourceTypes: RecordLinkOwnerType[];
  targetTypes: RecordLinkOwnerType[];
  isActive: boolean;
  sortOrder: number;
}

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  initialValues: RecordLinkTypeFormValues;
  saving?: boolean;
  /**
   * Błędy zwrócone przez ostatnią próbę zapisu (409 `CODE_TAKEN`), pokazywane
   * inline przy polu kodu — analogicznie do `DictionaryFormModal`. Caller ustawia
   * nowy obiekt (nawet o tej samej treści) przy każdej próbie, żeby `useEffect`
   * poniżej zawsze wykrył zmianę i odświeżył widoczny błąd.
   */
  serverErrors?: Record<string, string>;
  onSubmit: (values: RecordLinkTypeFormValues) => void;
  onClose: () => void;
}

const CODE_REGEX = /^[a-z][a-z0-9_]{0,48}$/;
const COLOR_REGEX = /^#[0-9a-fA-F]{6}$/;
/** Liczba całkowita, opcjonalny znak minus — `parseInt` sam w sobie ucina ułamki
 * (`"1.5"` → `1`) bez sygnalizacji błędu, więc walidacja musi sprawdzić kształt
 * stringa PRZED konwersją, nie tylko `Number.isNaN` wyniku `parseInt`. */
const INTEGER_REGEX = /^-?\d+$/;
const CARDINALITIES: RecordLinkCardinality[] = ['one_to_one', 'one_to_many', 'many_to_many'];
const OWNER_TYPES: RecordLinkOwnerType[] = [
  'contractor',
  'employee',
  'product',
  'document',
  'task',
];

export function RecordLinkTypeFormModal({
  open,
  mode,
  initialValues,
  saving,
  serverErrors,
  onSubmit,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [code, setCode] = useState(initialValues.code);
  const [forwardLabel, setForwardLabel] = useState(initialValues.forwardLabel);
  const [reverseLabel, setReverseLabel] = useState(initialValues.reverseLabel);
  const [isSymmetric, setIsSymmetric] = useState(initialValues.isSymmetric);
  const [cardinality, setCardinality] = useState<RecordLinkCardinality>(initialValues.cardinality);
  const [color, setColor] = useState(initialValues.color);
  const [icon, setIcon] = useState(initialValues.icon);
  const [sourceTypes, setSourceTypes] = useState<RecordLinkOwnerType[]>(initialValues.sourceTypes);
  const [targetTypes, setTargetTypes] = useState<RecordLinkOwnerType[]>(initialValues.targetTypes);
  const [isActive, setIsActive] = useState(initialValues.isActive);
  const [sortOrderStr, setSortOrderStr] = useState(String(initialValues.sortOrder));
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setCode(initialValues.code);
      setForwardLabel(initialValues.forwardLabel);
      setReverseLabel(initialValues.reverseLabel);
      setIsSymmetric(initialValues.isSymmetric);
      setCardinality(initialValues.cardinality);
      setColor(initialValues.color);
      setIcon(initialValues.icon);
      setSourceTypes(initialValues.sourceTypes);
      setTargetTypes(initialValues.targetTypes);
      setIsActive(initialValues.isActive);
      setSortOrderStr(String(initialValues.sortOrder));
      setErrors({});
    }
  }, [open, initialValues]);

  useEffect(() => {
    if (serverErrors) {
      setErrors((prev) => ({ ...prev, ...serverErrors }));
    }
  }, [serverErrors]);

  // „Zmieniono" dla zabezpieczenia zamknięcia: kardynalność, typy, ikonę, kolor i aktywność
  // wybiera się kliknięciem (bez zdarzeń wpisywania), więc porównujemy wartości formularza
  // ze stanem startowym.
  const isDirty =
    JSON.stringify([
      code,
      forwardLabel,
      reverseLabel,
      isSymmetric,
      cardinality,
      color,
      icon,
      sourceTypes,
      targetTypes,
      isActive,
      sortOrderStr,
    ]) !==
    JSON.stringify([
      initialValues.code,
      initialValues.forwardLabel,
      initialValues.reverseLabel,
      initialValues.isSymmetric,
      initialValues.cardinality,
      initialValues.color,
      initialValues.icon,
      initialValues.sourceTypes,
      initialValues.targetTypes,
      initialValues.isActive,
      String(initialValues.sortOrder),
    ]);

  const ownerTypeOptions = OWNER_TYPES.map((ot) => ({
    value: ot,
    label: t(`tenant.recordLinkTypes.ownerType.${ot}`),
  }));

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (mode === 'create') {
      if (!code) next.code = t('tenant.recordLinkTypes.form.errors.codeRequired');
      else if (!CODE_REGEX.test(code))
        next.code = t('tenant.recordLinkTypes.form.errors.codeInvalid');
    }
    if (!forwardLabel.trim())
      next.forwardLabel = t('tenant.recordLinkTypes.form.errors.forwardLabelRequired');
    else if (forwardLabel.length > 100)
      next.forwardLabel = t('tenant.recordLinkTypes.form.errors.forwardLabelLength');
    if (!isSymmetric && reverseLabel.length > 100)
      next.reverseLabel = t('tenant.recordLinkTypes.form.errors.reverseLabelLength');
    if (color && !COLOR_REGEX.test(color))
      next.color = t('tenant.recordLinkTypes.form.errors.colorInvalid');
    if (icon.length > 50) next.icon = t('tenant.recordLinkTypes.form.errors.iconLength');
    const trimmedSortOrder = sortOrderStr.trim();
    if (trimmedSortOrder && !INTEGER_REGEX.test(trimmedSortOrder))
      next.sortOrder = t('tenant.recordLinkTypes.form.errors.sortOrderInvalid');
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    onSubmit({
      code: code.trim(),
      forwardLabel: forwardLabel.trim(),
      reverseLabel: isSymmetric ? '' : reverseLabel.trim(),
      isSymmetric,
      cardinality,
      color: color.trim(),
      icon: icon.trim(),
      sourceTypes,
      targetTypes,
      isActive,
      sortOrder: sortOrderStr.trim() ? parseInt(sortOrderStr, 10) : 0,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        mode === 'create'
          ? t('tenant.recordLinkTypes.form.titleCreate')
          : t('tenant.recordLinkTypes.form.titleEdit')
      }
      size="md"
      testId="record-link-type-form-modal"
      guardUnsaved
      dirty={isDirty}
      // W trakcie zapisu tło i Esc nie zamykają okna („Anuluj" jest wyłączone; X w nagłówku zostaje).
      closeOnBackdrop={!saving}
      closeOnEsc={!saving}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={saving}
            data-testid="record-link-type-form-submit"
          >
            {mode === 'create' ? t('common.create') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <FormField
          label={t('tenant.recordLinkTypes.form.code')}
          error={errors.code}
          hint={mode === 'edit' ? t('tenant.recordLinkTypes.form.codeImmutableHint') : undefined}
        >
          <Input
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              setErrors((prev) => ({ ...prev, code: '' }));
            }}
            disabled={mode === 'edit'}
            placeholder="np. powiazany_z"
            data-testid="record-link-type-form-code"
          />
        </FormField>
        <FormField
          label={t('tenant.recordLinkTypes.form.forwardLabel')}
          error={errors.forwardLabel}
        >
          <Input
            value={forwardLabel}
            onChange={(e) => setForwardLabel(e.target.value)}
            placeholder="np. Powiązany z"
            data-testid="record-link-type-form-forward-label"
          />
        </FormField>
        <Checkbox
          label={t('tenant.recordLinkTypes.form.isSymmetric')}
          hint={t('tenant.recordLinkTypes.form.isSymmetricHint')}
          checked={isSymmetric}
          onChange={(e) => setIsSymmetric(e.target.checked)}
          data-testid="record-link-type-form-symmetric"
        />
        <FormField
          label={t('tenant.recordLinkTypes.form.reverseLabel')}
          error={errors.reverseLabel}
          hint={isSymmetric ? t('tenant.recordLinkTypes.form.reverseLabelDisabledHint') : undefined}
        >
          <Input
            value={isSymmetric ? '' : reverseLabel}
            onChange={(e) => setReverseLabel(e.target.value)}
            disabled={isSymmetric}
            placeholder="np. Dotyczy go"
            data-testid="record-link-type-form-reverse-label"
          />
        </FormField>
        <FormField label={t('tenant.recordLinkTypes.form.cardinality')}>
          <Dropdown
            value={cardinality}
            onChange={(v) => setCardinality(v as RecordLinkCardinality)}
            data-testid="record-link-type-form-cardinality"
            options={CARDINALITIES.map((c) => ({
              value: c,
              label: t(`tenant.recordLinkTypes.cardinality.${c}`),
            }))}
          />
        </FormField>
        <FormField label={t('tenant.recordLinkTypes.form.color')} error={errors.color}>
          <div className="flex items-center gap-2">
            <Input
              value={color}
              onChange={(e) => setColor(e.target.value)}
              placeholder="#3B82F6"
              data-testid="record-link-type-form-color"
            />
            <input
              type="color"
              value={COLOR_REGEX.test(color) ? color : '#3B82F6'}
              onChange={(e) => setColor(e.target.value.toUpperCase())}
              className="h-9 w-12 cursor-pointer rounded border border-border-primary"
              aria-label={t('tenant.recordLinkTypes.form.colorPicker')}
            />
          </div>
        </FormField>
        <FormField
          label={t('tenant.recordLinkTypes.form.icon')}
          error={errors.icon}
          hint={t('tenant.recordLinkTypes.form.iconHint')}
        >
          <IconPicker
            value={icon ? icon.charAt(0).toUpperCase() + icon.slice(1) : ''}
            onChange={(name) => setIcon(name)}
            ariaLabel={t('tenant.recordLinkTypes.form.icon')}
          />
        </FormField>
        <FormField
          label={t('tenant.recordLinkTypes.form.sourceTypes')}
          hint={t('tenant.recordLinkTypes.form.ownerTypesEmptyHint')}
        >
          <MultiSelect
            value={sourceTypes}
            onChange={(next) => setSourceTypes(next as RecordLinkOwnerType[])}
            options={ownerTypeOptions}
            data-testid="record-link-type-form-source-types"
          />
        </FormField>
        <FormField
          label={t('tenant.recordLinkTypes.form.targetTypes')}
          hint={t('tenant.recordLinkTypes.form.ownerTypesEmptyHint')}
        >
          <MultiSelect
            value={targetTypes}
            onChange={(next) => setTargetTypes(next as RecordLinkOwnerType[])}
            options={ownerTypeOptions}
            data-testid="record-link-type-form-target-types"
          />
        </FormField>
        <FormField label={t('tenant.recordLinkTypes.form.sortOrder')} error={errors.sortOrder}>
          <Input
            type="number"
            value={sortOrderStr}
            onChange={(e) => setSortOrderStr(e.target.value)}
            data-testid="record-link-type-form-sort-order"
          />
        </FormField>
        <Checkbox
          label={t('tenant.recordLinkTypes.form.isActive')}
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          data-testid="record-link-type-form-active"
        />
      </div>
    </Modal>
  );
}
